import { useEffect, useState } from "react";
import { withStyles } from "@bruitt/classnames";

import { ColorProps } from "../../types/colors";
import { IconSizesProps } from "../../types/icons";

import {
  EAGER_ICON_TYPE_MAP,
  IconComponent,
  IconName,
  LAZY_ICON_LOADERS,
  isEagerIconName,
} from "./icon-type-map";

import styles from "./icon.module.scss";

const sx = withStyles(styles);
const lazyIconCache = new Map<IconName, IconComponent>();

type ElementProps = Omit<JSX.IntrinsicElements["svg"], "color">;
interface IconProps extends ElementProps, IconSizesProps, ColorProps {
  name: IconName;
}

export const Icon = ({
  name,
  className,
  s = "24",
  color,
  ...rest
}: IconProps) => {
  const eagerComponent = isEagerIconName(name)
    ? EAGER_ICON_TYPE_MAP[name]
    : null;
  const [lazyComponent, setLazyComponent] = useState<IconComponent | null>(
    () => (!eagerComponent ? lazyIconCache.get(name) ?? null : null),
  );

  useEffect(() => {
    if (eagerComponent) {
      return;
    }

    const cachedComponent = lazyIconCache.get(name);

    if (cachedComponent) {
      setLazyComponent(() => cachedComponent);
      return;
    }

    setLazyComponent(null);

    let isCancelled = false;
    const loadIcon = LAZY_ICON_LOADERS[name as keyof typeof LAZY_ICON_LOADERS];

    void loadIcon().then((Component: IconComponent) => {
      lazyIconCache.set(name, Component);

      if (!isCancelled) {
        setLazyComponent(() => Component);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [eagerComponent, name]);

  const Component = eagerComponent || lazyComponent;

  if (!Component) {
    return (
      <svg
        className={sx(className, { color })}
        width={s}
        height={s}
        aria-hidden="true"
        {...rest}
      />
    );
  }

  return (
    <Component
      className={sx(className, { color })}
      {...rest}
      width={s}
      height={s}
    />
  );
};

export type { IconName } from "./icon-type-map";
